import { useCallback, useEffect, useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'

/** 레슨 채점 화면 표시 방식 — grading: 채점표(기본), coaching: 기존 코칭 화면. */
export type CoachingGradingMode = 'grading' | 'coaching'

const STORAGE_PREFIX = 'lc:coaching:grading-mode:v1'
const DEFAULT_MODE: CoachingGradingMode = 'grading'

function buildStorageKey(tenantId: string | null, userId: number | null): string | null {
  if (!tenantId || userId == null) return null
  return `${STORAGE_PREFIX}:${tenantId}:${userId}`
}

function isGradingMode(value: unknown): value is CoachingGradingMode {
  return value === 'grading' || value === 'coaching'
}

function readMode(storageKey: string | null): CoachingGradingMode {
  if (!storageKey) return DEFAULT_MODE
  try {
    const raw = localStorage.getItem(storageKey)
    return isGradingMode(raw) ? raw : DEFAULT_MODE
  } catch {
    return DEFAULT_MODE
  }
}

function writeMode(storageKey: string | null, mode: CoachingGradingMode): void {
  if (!storageKey) return
  try {
    localStorage.setItem(storageKey, mode)
  } catch {
    // 브라우저 저장소가 차단돼도 현재 화면의 모드는 유지한다.
  }
}

/**
 * 채점/코칭 모드 선호 — 교사(tenant+user)별 마지막 선택을 localStorage에 기억한다.
 * 기본은 채점모드. tenant/user가 확정되지 않았으면 메모리 state만 쓴다.
 */
export function usePersistedGradingMode(): {
  mode: CoachingGradingMode
  setMode: (mode: CoachingGradingMode) => void
} {
  const tenantId = useTenantStore((state) => state.tenantId)
  const userId = useAuthStore((state) => state.user?.id ?? null)
  const storageKey = buildStorageKey(tenantId, userId)
  const [mode, setModeState] = useState<CoachingGradingMode>(() => readMode(storageKey))

  useEffect(() => {
    setModeState(readMode(storageKey))
  }, [storageKey])

  const setMode = useCallback((next: CoachingGradingMode) => {
    writeMode(storageKey, next)
    setModeState(next)
  }, [storageKey])

  return { mode, setMode }
}
